import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Search,
  BookOpen,
  MapPin,
  Clock,
  DollarSign,
  GraduationCap,
  Filter,
  LayoutGrid,
  LayoutList,
  ChevronLeft,
  ChevronRight,
  Heart,
  Plus,
  Eye,
  X,
  Trash2,
  Globe,
  Languages,
  Award,
  Building,
  Calendar,
  ExternalLink,
  Inbox,
  Loader2,
  SlidersHorizontal,
  Star
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { agentAPI, admissionsAPI } from '../../services/api';
import { useRoleBasePath } from '../../hooks/useRoleBasePath';

// ============================================
// FILTER SIDEBAR
// ============================================

function FilterSidebar({ filters, filterOptions, onFilterChange, onClear, loading }) {
  const updateFilter = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4" />
          Filters
        </h3>
        <Button variant="ghost" size="sm" onClick={onClear} className="text-xs text-gray-500 h-7">
          Clear All
        </Button>
      </div>

      <Separator />

      {/* Country */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-600">Country</Label>
        <Select value={filters.country || ''} onValueChange={(v) => updateFilter('country', v === '__all__' ? '' : v)}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="All Countries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Countries</SelectItem>
            {(filterOptions.countries || []).map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* University */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-600">University</Label>
        <Input
          placeholder="Search university..."
          value={filters.universityName || ''}
          onChange={(e) => updateFilter('universityName', e.target.value)}
          className="h-9 text-sm"
        />
      </div>

      {/* Program Level */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-600">Program Level</Label>
        <Select value={filters.programLevel || ''} onValueChange={(v) => updateFilter('programLevel', v === '__all__' ? '' : v)}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="All Levels" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Levels</SelectItem>
            {(filterOptions.levels || []).map(l => (
              <SelectItem key={l} value={l}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Discipline / Majors */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-600">Discipline / Major</Label>
        <Input
          placeholder="e.g. Computer Science"
          value={filters.majors || ''}
          onChange={(e) => updateFilter('majors', e.target.value)}
          className="h-9 text-sm"
        />
      </div>

      {/* Intake */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-600">Intake</Label>
        <Select value={filters.intake || ''} onValueChange={(v) => updateFilter('intake', v === '__all__' ? '' : v)}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="All Intakes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Intakes</SelectItem>
            {(filterOptions.intakes || []).map(i => (
              <SelectItem key={i} value={i}>{i}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Duration */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-600">Duration</Label>
        <Select value={filters.duration || ''} onValueChange={(v) => updateFilter('duration', v === '__all__' ? '' : v)}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="All Durations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Durations</SelectItem>
            {(filterOptions.durations || []).map(d => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Program Mode */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-600">Program Mode</Label>
        <Select value={filters.programMode || ''} onValueChange={(v) => updateFilter('programMode', v === '__all__' ? '' : v)}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="All Modes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Modes</SelectItem>
            {(filterOptions.modes || []).map(m => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* Tuition Range */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-600">Tuition Fee Range</Label>
        <div className="flex gap-2">
          <Input
            type="number"
            placeholder="Min"
            value={filters.tuitionMin || ''}
            onChange={(e) => updateFilter('tuitionMin', e.target.value)}
            className="h-9 text-sm"
          />
          <Input
            type="number"
            placeholder="Max"
            value={filters.tuitionMax || ''}
            onChange={(e) => updateFilter('tuitionMax', e.target.value)}
            className="h-9 text-sm"
          />
        </div>
      </div>

      {/* Application Fee Max */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-600">Max Application Fee</Label>
        <Input
          type="number"
          placeholder="e.g. 100"
          value={filters.applicationFeeMax || ''}
          onChange={(e) => updateFilter('applicationFeeMax', e.target.value)}
          className="h-9 text-sm"
        />
      </div>

      <Separator />

      {/* Language Requirements */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-600">Min IELTS Score</Label>
        <Input
          type="number"
          step="0.5"
          placeholder="e.g. 6.5"
          value={filters.ieltsMin || ''}
          onChange={(e) => updateFilter('ieltsMin', e.target.value)}
          className="h-9 text-sm"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-600">Min TOEFL Score</Label>
        <Input
          type="number"
          placeholder="e.g. 80"
          value={filters.toeflMin || ''}
          onChange={(e) => updateFilter('toeflMin', e.target.value)}
          className="h-9 text-sm"
        />
      </div>

      {/* Scholarship */}
      <div className="flex items-center gap-2 pt-1">
        <Checkbox
          id="scholarship"
          checked={filters.scholarship === 'true'}
          onCheckedChange={(checked) => updateFilter('scholarship', checked ? 'true' : '')}
        />
        <Label htmlFor="scholarship" className="text-sm text-gray-700 cursor-pointer">
          Scholarship Available
        </Label>
      </div>
    </div>
  );
}

// ============================================
// PROGRAM CARD (Grid View)
// ============================================

function ProgramCard({ program, onViewDetails, onShortlist, onAddToStudent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="p-5 bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all h-full flex flex-col">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm leading-tight truncate" title={program.programName}>
              {program.programName}
            </h3>
            <p className="text-emerald-600 font-medium text-xs mt-0.5 truncate" title={program.universityName}>
              {program.universityName}
            </p>
          </div>
          <Badge variant="outline" className="ml-2 shrink-0 text-[10px] px-1.5 py-0.5">
            {program.programLevel?.replace(' Program', '')}
          </Badge>
        </div>

        <div className="space-y-1.5 text-xs text-gray-600 flex-1">
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 shrink-0 text-gray-400" />
            <span className="truncate">{program.country}{program.location ? `, ${program.location}` : ''}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 shrink-0 text-gray-400" />
            <span>{program.duration}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 shrink-0 text-gray-400" />
            <span className="truncate">{program.intake}</span>
          </div>
          {program.tuitionFee && (
            <div className="flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5 shrink-0 text-gray-400" />
              <span className="truncate">{program.tuitionFee}</span>
            </div>
          )}
          {program.languageRequirement?.ielts && (
            <div className="flex items-center gap-1.5">
              <Languages className="w-3.5 h-3.5 shrink-0 text-gray-400" />
              <span>IELTS: {program.languageRequirement.ielts}</span>
            </div>
          )}
          {program.scholarship && (
            <div className="flex items-center gap-1.5">
              <Award className="w-3.5 h-3.5 shrink-0 text-emerald-500" />
              <span className="text-emerald-600 truncate">{program.scholarship}</span>
            </div>
          )}
        </div>

        <Separator className="my-3" />

        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={() => onViewDetails(program)}>
            <Eye className="w-3 h-3 mr-1" /> Details
          </Button>
          <Button variant="outline" size="sm" className="h-8 px-2.5" onClick={() => onShortlist(program)} title="Shortlist">
            <Heart className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" className="flex-1 h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => onAddToStudent(program)}>
            <Plus className="w-3 h-3 mr-1" /> Apply
          </Button>
        </div>
      </Card>
    </motion.div>
  );
}

// ============================================
// PROGRAM DETAIL SHEET
// ============================================

function ProgramDetailSheet({ open, onClose, programId }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && programId) {
      setLoading(true);
      agentAPI.getCourseDetail(programId)
        .then(res => setDetail(res.data))
        .catch(() => toast.error('Failed to load program details'))
        .finally(() => setLoading(false));
    } else {
      setDetail(null);
    }
  }, [open, programId]);

  const p = detail?.program;
  const u = detail?.university;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-lg">Program Details</SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="space-y-4 mt-6">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : p ? (
          <div className="mt-6 space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-start gap-3">
                {u?.imageUrl ? (
                  <img src={u.imageUrl} alt={u.universityName} className="w-12 h-12 rounded-lg object-cover border" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Building className="w-6 h-6 text-emerald-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="font-bold text-gray-900 text-base leading-tight">{p.programName}</h2>
                  <p className="text-emerald-600 font-medium text-sm">{p.universityName}</p>
                  {u?.ranking && (
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                      <span className="text-xs text-gray-600">Rank #{u.ranking}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Info */}
            <Card className="p-4 bg-gray-50 border-0">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <InfoRow icon={MapPin} label="Country" value={`${p.country}${p.location ? ', ' + p.location : ''}`} />
                <InfoRow icon={GraduationCap} label="Level" value={p.programLevel} />
                <InfoRow icon={Clock} label="Duration" value={p.duration} />
                <InfoRow icon={Calendar} label="Intake" value={p.intake} />
                {p.tuitionFee && <InfoRow icon={DollarSign} label="Tuition" value={p.tuitionFee} />}
                {p.applicationFee && <InfoRow icon={DollarSign} label="App Fee" value={p.applicationFee} />}
                {p.programMode && <InfoRow icon={Globe} label="Mode" value={p.programMode} />}
                {p.majors && <InfoRow icon={BookOpen} label="Majors" value={p.majors} />}
              </div>
            </Card>

            {/* Language Requirements */}
            {p.languageRequirement && (p.languageRequirement.ielts || p.languageRequirement.toefl || p.languageRequirement.pte || p.languageRequirement.duolingo) && (
              <div>
                <h3 className="font-semibold text-sm text-gray-900 mb-2 flex items-center gap-1.5">
                  <Languages className="w-4 h-4" /> Language Requirements
                </h3>
                <Card className="p-3 border-0 bg-blue-50">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {p.languageRequirement.ielts && <span>IELTS: <strong>{p.languageRequirement.ielts}</strong></span>}
                    {p.languageRequirement.toefl && <span>TOEFL: <strong>{p.languageRequirement.toefl}</strong></span>}
                    {p.languageRequirement.pte && <span>PTE: <strong>{p.languageRequirement.pte}</strong></span>}
                    {p.languageRequirement.duolingo && <span>Duolingo: <strong>{p.languageRequirement.duolingo}</strong></span>}
                  </div>
                </Card>
              </div>
            )}

            {/* Scholarship */}
            {p.scholarship && (
              <div>
                <h3 className="font-semibold text-sm text-gray-900 mb-2 flex items-center gap-1.5">
                  <Award className="w-4 h-4" /> Scholarship
                </h3>
                <Card className="p-3 border-0 bg-emerald-50">
                  <p className="text-sm text-emerald-800">{p.scholarship}</p>
                </Card>
              </div>
            )}

            {/* University Scholarships */}
            {u?.scholarships?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-gray-900 mb-2">University Scholarships</h3>
                <div className="space-y-2">
                  {u.scholarships.slice(0, 5).map((s, i) => (
                    <Card key={i} className="p-3 border-0 bg-amber-50">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-gray-900">{s.name}</span>
                        {s.amount && <Badge variant="outline" className="text-xs">{s.amount}</Badge>}
                      </div>
                      {s.eligibility && <p className="text-xs text-gray-600 mt-1">{s.eligibility}</p>}
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* University Requirements */}
            {u?.generalRequirements?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-gray-900 mb-2">General Requirements</h3>
                <ul className="space-y-1 text-sm text-gray-700 list-disc pl-4">
                  {u.generalRequirements.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}

            {/* University Website */}
            {u?.website && (
              <a
                href={u.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Visit University Website
              </a>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
            No data available
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-1.5">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-gray-400 shrink-0" />
      <div className="min-w-0">
        <span className="text-[10px] text-gray-500 block leading-none">{label}</span>
        <span className="text-gray-800 text-xs leading-tight block truncate">{value}</span>
      </div>
    </div>
  );
}

// ============================================
// SHORTLIST DIALOG
// ============================================

function ShortlistDialog({ open, onClose, program, students, onConfirm }) {
  const [selectedStudent, setSelectedStudent] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    if (!selectedStudent) {
      toast.error('Please select a student');
      return;
    }
    setSaving(true);
    try {
      await onConfirm(selectedStudent, program._id, notes);
      setSelectedStudent('');
      setNotes('');
      onClose();
    } catch {
      // error handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Shortlist Program</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="font-medium text-sm text-gray-900">{program?.programName}</p>
            <p className="text-xs text-gray-500">{program?.universityName} - {program?.country}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Select Student</Label>
            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a student..." />
              </SelectTrigger>
              <SelectContent>
                {students.map(s => (
                  <SelectItem key={s.studentId} value={s.studentId}>
                    {s.firstName} {s.lastName} ({s.studentId})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Notes (optional)</Label>
            <Input
              placeholder="Add a note..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Shortlist
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// ADD TO STUDENT DIALOG
// ============================================

function AddToStudentDialog({ open, onClose, program, students }) {
  const navigate = useNavigate();
  const [selectedStudent, setSelectedStudent] = useState('');
  const [intake, setIntake] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open && program) {
      setIntake(program.intake || '');
    }
  }, [open, program]);

  const handleCreate = async () => {
    if (!selectedStudent) {
      toast.error('Please select a student');
      return;
    }
    setCreating(true);
    try {
      await admissionsAPI.create({
        studentId: selectedStudent,
        universityName: program.universityName,
        universityCode: program.universityCode || '',
        programName: program.programName,
        programLevel: program.programLevel || '',
        intake: intake || program.intake
      });
      toast.success('Application created successfully');
      setSelectedStudent('');
      setIntake('');
      onClose();
      navigate(`${basePath}/admissions`);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to create application';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Application</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="p-3 bg-gray-50 rounded-lg space-y-1">
            <p className="font-medium text-sm text-gray-900">{program?.programName}</p>
            <p className="text-xs text-gray-500">{program?.universityName} - {program?.country}</p>
            {program?.programLevel && (
              <Badge variant="outline" className="text-[10px] mt-1">{program.programLevel}</Badge>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Select Student *</Label>
            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a student..." />
              </SelectTrigger>
              <SelectContent>
                {students.map(s => (
                  <SelectItem key={s.studentId} value={s.studentId}>
                    {s.firstName} {s.lastName} ({s.studentId})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Intake *</Label>
            <Input
              value={intake}
              onChange={(e) => setIntake(e.target.value)}
              placeholder="e.g. Fall 2025"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700">
            {creating && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Create Application
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// PAGINATION COMPONENT
// ============================================

function Pagination({ pagination, onPageChange }) {
  const { page, totalPages, total } = pagination;
  if (totalPages <= 1) return null;

  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, page - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="flex items-center justify-between pt-4">
      <span className="text-xs text-gray-500">
        {total} program{total !== 1 ? 's' : ''} found
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="h-8 w-8 p-0"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        {pages.map(p => (
          <Button
            key={p}
            variant={p === page ? 'default' : 'outline'}
            size="sm"
            onClick={() => onPageChange(p)}
            className={`h-8 w-8 p-0 text-xs ${p === page ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
          >
            {p}
          </Button>
        ))}
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="h-8 w-8 p-0"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

const DEFAULT_FILTERS = {
  country: '',
  universityName: '',
  programLevel: '',
  majors: '',
  intake: '',
  duration: '',
  programMode: '',
  tuitionMin: '',
  tuitionMax: '',
  applicationFeeMax: '',
  ieltsMin: '',
  toeflMin: '',
  scholarship: ''
};

export default function AgentCourseFinder() {
  const navigate = useNavigate();
  const basePath = useRoleBasePath();

  // Search & Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState({});
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Programs
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });

  // Detail Sheet
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailProgramId, setDetailProgramId] = useState(null);

  // Shortlist Dialog
  const [shortlistOpen, setShortlistOpen] = useState(false);
  const [shortlistProgram, setShortlistProgram] = useState(null);

  // Add to Student Dialog
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [addStudentProgram, setAddStudentProgram] = useState(null);

  // Students list (for dialogs)
  const [students, setStudents] = useState([]);

  // Shortlists tab
  const [shortlists, setShortlists] = useState([]);
  const [shortlistsLoading, setShortlistsLoading] = useState(false);
  const [shortlistPagination, setShortlistPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [shortlistStudentFilter, setShortlistStudentFilter] = useState('');

  const [activeTab, setActiveTab] = useState('search');

  // Debounce search
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  // Load filter options on mount
  useEffect(() => {
    agentAPI.getCourseFilters()
      .then(res => setFilterOptions(res.data || {}))
      .catch(() => { /* silent - filters just won't populate */ });
  }, []);

  // Load students on mount (for dialogs)
  useEffect(() => {
    agentAPI.getStudentsList()
      .then(res => setStudents(res.students || res.data || []))
      .catch(() => { /* silent */ });
  }, []);

  // Search programs
  const fetchPrograms = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (debouncedSearch) params.search = debouncedSearch;

      // Add non-empty filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      const res = await agentAPI.searchCourses(params);
      setPrograms(res.data || []);
      setPagination(res.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Failed to search programs');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters]);

  useEffect(() => {
    fetchPrograms(1);
  }, [fetchPrograms]);

  // Load shortlists
  const fetchShortlists = useCallback(async (page = 1) => {
    setShortlistsLoading(true);
    try {
      const params = { page, limit: 20 };
      if (shortlistStudentFilter) params.studentId = shortlistStudentFilter;
      const res = await agentAPI.getShortlists(params);
      setShortlists(res.data || []);
      setShortlistPagination(res.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
    } catch {
      toast.error('Failed to load shortlists');
    } finally {
      setShortlistsLoading(false);
    }
  }, [shortlistStudentFilter]);

  useEffect(() => {
    if (activeTab === 'shortlisted') fetchShortlists(1);
  }, [activeTab, fetchShortlists]);

  // Handlers
  const handleViewDetails = (program) => {
    setDetailProgramId(program._id);
    setDetailOpen(true);
  };

  const handleOpenShortlist = (program) => {
    setShortlistProgram(program);
    setShortlistOpen(true);
  };

  const handleShortlistConfirm = async (studentId, programId, notes) => {
    try {
      await agentAPI.addToShortlist({ studentId, programId, notes });
      toast.success('Program shortlisted');
      if (activeTab === 'shortlisted') fetchShortlists(shortlistPagination.page);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to shortlist';
      toast.error(msg);
      throw err;
    }
  };

  const handleRemoveShortlist = async (shortlistId) => {
    try {
      await agentAPI.removeFromShortlist(shortlistId);
      toast.success('Removed from shortlist');
      fetchShortlists(shortlistPagination.page);
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleOpenAddStudent = (program) => {
    setAddStudentProgram(program);
    setAddStudentOpen(true);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchTerm('');
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <AgentLayout>
      <div className="space-y-4" data-testid="agent-course-finder">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Course & Program Discovery</h1>
            <p className="text-gray-500 text-sm">Search, filter, and shortlist programs for your students</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-100">
            <TabsTrigger value="search" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <Search className="w-3.5 h-3.5 mr-1.5" /> Search Programs
            </TabsTrigger>
            <TabsTrigger value="shortlisted" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <Heart className="w-3.5 h-3.5 mr-1.5" /> Shortlisted
              {shortlistPagination.total > 0 && (
                <Badge className="ml-1.5 h-5 px-1.5 text-[10px] bg-emerald-100 text-emerald-700">
                  {shortlistPagination.total}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ============================================ */}
          {/* SEARCH TAB */}
          {/* ============================================ */}
          <TabsContent value="search" className="mt-4">
            {/* Search Bar */}
            <Card className="p-3 bg-white border-0 shadow-sm mb-4">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search programs, universities, countries, majors..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-gray-200 h-10"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  {/* Mobile filter toggle */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="lg:hidden h-10 relative"
                    onClick={() => setMobileFiltersOpen(true)}
                  >
                    <Filter className="w-4 h-4" />
                    {activeFilterCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-600 text-white text-[9px] rounded-full flex items-center justify-center">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'outline'}
                    size="sm"
                    className={`h-10 w-10 p-0 ${viewMode === 'grid' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'table' ? 'default' : 'outline'}
                    size="sm"
                    className={`h-10 w-10 p-0 ${viewMode === 'table' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                    onClick={() => setViewMode('table')}
                  >
                    <LayoutList className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>

            <div className="flex gap-4">
              {/* Desktop Filter Sidebar */}
              <div className="hidden lg:block w-64 shrink-0">
                <Card className="p-4 bg-white border-0 shadow-sm sticky top-4">
                  <ScrollArea className="max-h-[calc(100vh-200px)]">
                    <FilterSidebar
                      filters={filters}
                      filterOptions={filterOptions}
                      onFilterChange={setFilters}
                      onClear={handleClearFilters}
                    />
                  </ScrollArea>
                </Card>
              </div>

              {/* Mobile Filter Sheet */}
              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetContent side="left" className="w-80 overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-4">
                    <FilterSidebar
                      filters={filters}
                      filterOptions={filterOptions}
                      onFilterChange={(f) => { setFilters(f); }}
                      onClear={() => { handleClearFilters(); setMobileFiltersOpen(false); }}
                    />
                    <Button
                      className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      Apply Filters
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>

              {/* Results */}
              <div className="flex-1 min-w-0">
                {/* Active Filters Chips */}
                {activeFilterCount > 0 && (
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className="text-xs text-gray-500">Active filters:</span>
                    {Object.entries(filters).map(([key, value]) => {
                      if (!value) return null;
                      const labels = {
                        country: 'Country', universityName: 'University', programLevel: 'Level',
                        majors: 'Major', intake: 'Intake', duration: 'Duration', programMode: 'Mode',
                        tuitionMin: 'Min Tuition', tuitionMax: 'Max Tuition', applicationFeeMax: 'Max App Fee',
                        ieltsMin: 'Min IELTS', toeflMin: 'Min TOEFL', scholarship: 'Scholarship'
                      };
                      const display = key === 'scholarship' ? 'Available' : value;
                      return (
                        <Badge key={key} variant="secondary" className="text-xs gap-1 pr-1">
                          {labels[key]}: {display}
                          <button
                            onClick={() => setFilters(prev => ({ ...prev, [key]: '' }))}
                            className="ml-0.5 hover:bg-gray-300 rounded-full p-0.5"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </Badge>
                      );
                    })}
                    <Button variant="ghost" size="sm" onClick={handleClearFilters} className="h-6 text-xs text-gray-500">
                      Clear all
                    </Button>
                  </div>
                )}

                {loading ? (
                  viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <Card key={i} className="p-5">
                          <Skeleton className="h-4 w-3/4 mb-2" />
                          <Skeleton className="h-3 w-1/2 mb-4" />
                          <Skeleton className="h-3 w-full mb-1.5" />
                          <Skeleton className="h-3 w-full mb-1.5" />
                          <Skeleton className="h-3 w-2/3 mb-4" />
                          <Skeleton className="h-8 w-full" />
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="bg-white border-0 shadow-sm overflow-hidden">
                      <div className="p-4 space-y-3">
                        {Array.from({ length: 8 }).map((_, i) => (
                          <Skeleton key={i} className="h-10 w-full" />
                        ))}
                      </div>
                    </Card>
                  )
                ) : programs.length === 0 ? (
                  <Card className="p-12 bg-white border-0 shadow-sm text-center">
                    <Inbox className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <h3 className="font-semibold text-gray-900 mb-1">No programs found</h3>
                    <p className="text-sm text-gray-500 mb-4">Try adjusting your search or filters</p>
                    <Button variant="outline" onClick={handleClearFilters}>Clear Filters</Button>
                  </Card>
                ) : viewMode === 'grid' ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="courses-grid">
                      <AnimatePresence mode="popLayout">
                        {programs.map(program => (
                          <ProgramCard
                            key={program._id}
                            program={program}
                            onViewDetails={handleViewDetails}
                            onShortlist={handleOpenShortlist}
                            onAddToStudent={handleOpenAddStudent}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                    <Pagination pagination={pagination} onPageChange={(p) => fetchPrograms(p)} />
                  </>
                ) : (
                  <>
                    <Card className="bg-white border-0 shadow-sm overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Program</TableHead>
                            <TableHead className="text-xs">University</TableHead>
                            <TableHead className="text-xs hidden md:table-cell">Country</TableHead>
                            <TableHead className="text-xs hidden lg:table-cell">Level</TableHead>
                            <TableHead className="text-xs hidden lg:table-cell">Intake</TableHead>
                            <TableHead className="text-xs hidden xl:table-cell">Tuition</TableHead>
                            <TableHead className="text-xs hidden xl:table-cell">Duration</TableHead>
                            <TableHead className="text-xs text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {programs.map(program => (
                            <TableRow key={program._id} className="hover:bg-gray-50">
                              <TableCell className="max-w-[200px]">
                                <span className="font-medium text-sm text-gray-900 truncate block">{program.programName}</span>
                                {program.majors && <span className="text-[11px] text-gray-500 truncate block">{program.majors}</span>}
                              </TableCell>
                              <TableCell className="text-sm text-gray-700 max-w-[150px] truncate">{program.universityName}</TableCell>
                              <TableCell className="text-sm text-gray-600 hidden md:table-cell">{program.country}</TableCell>
                              <TableCell className="hidden lg:table-cell">
                                <Badge variant="outline" className="text-[10px]">{program.programLevel?.replace(' Program', '')}</Badge>
                              </TableCell>
                              <TableCell className="text-xs text-gray-600 hidden lg:table-cell">{program.intake}</TableCell>
                              <TableCell className="text-xs text-gray-600 hidden xl:table-cell">{program.tuitionFee || '-'}</TableCell>
                              <TableCell className="text-xs text-gray-600 hidden xl:table-cell">{program.duration}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewDetails(program)} title="View Details">
                                    <Eye className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleOpenShortlist(program)} title="Shortlist">
                                    <Heart className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleOpenAddStudent(program)}>
                                    <Plus className="w-3 h-3 mr-1" /> Apply
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Card>
                    <Pagination pagination={pagination} onPageChange={(p) => fetchPrograms(p)} />
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ============================================ */}
          {/* SHORTLISTED TAB */}
          {/* ============================================ */}
          <TabsContent value="shortlisted" className="mt-4">
            <Card className="p-3 bg-white border-0 shadow-sm mb-4">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Select value={shortlistStudentFilter} onValueChange={(v) => setShortlistStudentFilter(v === '__all__' ? '' : v)}>
                    <SelectTrigger className="h-9 max-w-xs">
                      <SelectValue placeholder="Filter by student..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Students</SelectItem>
                      {students.map(s => (
                        <SelectItem key={s.studentId} value={s.studentId}>
                          {s.firstName} {s.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-xs text-gray-500">
                  {shortlistPagination.total} shortlisted program{shortlistPagination.total !== 1 ? 's' : ''}
                </span>
              </div>
            </Card>

            {shortlistsLoading ? (
              <Card className="bg-white border-0 shadow-sm overflow-hidden">
                <div className="p-4 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              </Card>
            ) : shortlists.length === 0 ? (
              <Card className="p-12 bg-white border-0 shadow-sm text-center">
                <Heart className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-1">No shortlisted programs</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Search programs and shortlist them for your students
                </p>
                <Button variant="outline" onClick={() => setActiveTab('search')}>
                  <Search className="w-3.5 h-3.5 mr-1.5" /> Search Programs
                </Button>
              </Card>
            ) : (
              <>
                <Card className="bg-white border-0 shadow-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Program</TableHead>
                        <TableHead className="text-xs">University</TableHead>
                        <TableHead className="text-xs hidden md:table-cell">Country</TableHead>
                        <TableHead className="text-xs hidden lg:table-cell">Student</TableHead>
                        <TableHead className="text-xs hidden lg:table-cell">Level</TableHead>
                        <TableHead className="text-xs hidden xl:table-cell">Added</TableHead>
                        <TableHead className="text-xs text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shortlists.map(entry => {
                        const prog = entry.program;
                        const stu = entry.student;
                        if (!prog) return null;
                        return (
                          <TableRow key={entry.shortlistId} className="hover:bg-gray-50">
                            <TableCell className="max-w-[200px]">
                              <span className="font-medium text-sm text-gray-900 truncate block">{prog.programName}</span>
                            </TableCell>
                            <TableCell className="text-sm text-gray-700 max-w-[150px] truncate">{prog.universityName}</TableCell>
                            <TableCell className="text-sm text-gray-600 hidden md:table-cell">{prog.country}</TableCell>
                            <TableCell className="hidden lg:table-cell">
                              {stu ? (
                                <span className="text-sm text-gray-700">{stu.firstName} {stu.lastName}</span>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <Badge variant="outline" className="text-[10px]">{prog.programLevel?.replace(' Program', '')}</Badge>
                            </TableCell>
                            <TableCell className="text-xs text-gray-500 hidden xl:table-cell">
                              {new Date(entry.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setDetailProgramId(prog._id); setDetailOpen(true); }} title="View Details">
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => handleOpenAddStudent(prog)}
                                >
                                  <Plus className="w-3 h-3 mr-1" /> Apply
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleRemoveShortlist(entry.shortlistId)}
                                  title="Remove"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>
                <Pagination pagination={shortlistPagination} onPageChange={(p) => fetchShortlists(p)} />
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Program Detail Sheet */}
        <ProgramDetailSheet
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          programId={detailProgramId}
        />

        {/* Shortlist Dialog */}
        <ShortlistDialog
          open={shortlistOpen}
          onClose={() => setShortlistOpen(false)}
          program={shortlistProgram}
          students={students}
          onConfirm={handleShortlistConfirm}
        />

        {/* Add to Student Dialog */}
        <AddToStudentDialog
          open={addStudentOpen}
          onClose={() => setAddStudentOpen(false)}
          program={addStudentProgram}
          students={students}
        />
      </div>
    </AgentLayout>
  );
}
